import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { Information } from '@opengov/react-capital-assets';
import { useQueryClient } from '@tanstack/react-query';
import uniqBy from 'lodash/uniqBy';
import React, { useEffect, useRef } from 'react';
import { FragmentType, getFragmentData, graphql } from '../../../../gql-generated/hasura';
import { SpreadsheetsProcessingStatus_Enum } from '../../../../gql-generated/hasura/graphql';
import { getCompletedLocaleText } from '../../../../locale/utils';
import locale from './customReports-locale.json';
import { styles } from './CustomReportsTableExpandedRow.styles';
import { customReportsQueryKeys } from './hooks/customReportsQueryKeys';
import { useFetchNamedRangesForExpandedRow } from './hooks/useFetchNamedRangesForExpandedRow';
import { TableCellSpinner } from './TableCellSpinner';
import { isProcessingCompleteOrFailed } from './CustomReportsTable.utils';

const CustomReportsTableExpandedRow_CustomReportFragment = graphql(`
  fragment CustomReportsTableExpandedRow_CustomReportFragment on spreadsheets {
    processingStatus
  }
`);

const CustomReportsTableExpandedRowStoryList_CustomReportFragment = graphql(`
  fragment CustomReportsTableExpandedRowStoryList_CustomReportFragment on spreadsheetNamedRanges {
    id
    name
    storyContentSpreadsheetReferences(distinct_on: storyContentId) {
      storyContentId
      storyContent {
        story {
          _id
          title
        }
      }
    }
  }
`);

type Props = {
  spreadsheetId: string;
  customReport: FragmentType<typeof CustomReportsTableExpandedRow_CustomReportFragment>;
};

export function CustomReportsTableExpandedRow({ spreadsheetId, customReport: customReportFragment }: Props) {
  const customReport = getFragmentData(CustomReportsTableExpandedRow_CustomReportFragment, customReportFragment);
  const { data: namedRangesFragment, isLoading: isLoadingNamedRanges } = useFetchNamedRangesForExpandedRow({
    spreadsheetId
  });
  const namedRangesData =
    getFragmentData(CustomReportsTableExpandedRowStoryList_CustomReportFragment, namedRangesFragment) ?? [];

  const namedRanges = namedRangesData.map((range) => ({
    id: range.id,
    name: range.name,
    stories: uniqBy(range.storyContentSpreadsheetReferences, (ref) => ref.storyContent.story._id).map(
      (ref) => ref.storyContent.story.title
    )
  }));

  const processingStatus = customReport.processingStatus;

  // Invalidate named ranges when processing completes
  useInvalidateNamedRangesOnProcessingComplete({ processingStatus, spreadsheetId });

  const isStillProcessingNamedRanges =
    processingStatus !== SpreadsheetsProcessingStatus_Enum.Completed && namedRanges.length === 0;

  return isLoadingNamedRanges || isStillProcessingNamedRanges ? (
    <ExpandedRowSkeleton />
  ) : (
    <>
      {namedRanges.map((namedRange) => (
        <TableRow key={namedRange.id}>
          <TableCell></TableCell>
          <TableCell>{namedRange.name}</TableCell>
          <TableCell colSpan={!isProcessingCompleteOrFailed(processingStatus) ? 1 : 3}>
            {namedRange.stories.length === 0 ? (
              locale.expandedRow.notUsedInAnyStories
            ) : namedRange.stories.length === 1 ? (
              <>{namedRange.stories[0]}</>
            ) : (
              <Tooltip
                title={
                  <div>
                    {namedRange.stories.map((story, index) => (
                      <span key={`story-${index}`} style={styles.storyTitleTooltip}>
                        {story}
                      </span>
                    ))}
                  </div>
                }
                placement="top"
              >
                <Box component="span" sx={styles.storiesCount}>
                  <Information />{' '}
                  {getCompletedLocaleText(locale.expandedRow.numStories, {
                    numStories: namedRange.stories.length
                  })}
                </Box>
              </Tooltip>
            )}
          </TableCell>
          {!isProcessingCompleteOrFailed(processingStatus) ? (
            <>
              <TableCell sx={styles.statusCell} data-test="custom-reports-table-status-cell">
                <TableCellSpinner />
              </TableCell>
              <TableCell sx={styles.actionsCell}></TableCell>
            </>
          ) : null}
        </TableRow>
      ))}
    </>
  );
}

function ExpandedRowSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }, (_, index) => (
        <TableRow key={`skeleton-${index}`}>
          <TableCell></TableCell>
          <TableCell>
            <Skeleton variant="text" width="70%" />
          </TableCell>
          <TableCell colSpan={3}>
            <Skeleton variant="text" width="60%" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * Hook to invalidate named ranges query when processing status transitions to completed
 */
export function useInvalidateNamedRangesOnProcessingComplete({
  processingStatus,
  spreadsheetId
}: {
  processingStatus: SpreadsheetsProcessingStatus_Enum;
  spreadsheetId: string;
}) {
  const queryClient = useQueryClient();
  const previousStatusRef = useRef(processingStatus);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    const currentStatus = processingStatus;

    // Check if status transitioned from processing to completed
    if (
      previousStatus !== SpreadsheetsProcessingStatus_Enum.Completed &&
      currentStatus === SpreadsheetsProcessingStatus_Enum.Completed
    ) {
      void queryClient.invalidateQueries({ queryKey: customReportsQueryKeys.namedRangesForTable(spreadsheetId) });
    }
    previousStatusRef.current = currentStatus;
  }, [processingStatus, spreadsheetId, queryClient]);
}
